import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { gql } from 'graphql-tag';
import { pick, set } from 'lodash';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  query: {
    where: {
      purchaseType: ['STOCK'],
      productGroupId: []
    },
    sort: {},
    offset: 0,
    limit: 10
  },
  table: {
    data: {
      count: 0,
      results: []
    },
    headers: [
      {
        text: 'Product ID',
        align: 'center',
        sortable: false,
        value: 'id'
      },
      {
        text: 'Product Image',
        align: 'center',
        sortable: false,
        value: 'image'
      },
      {
        text: 'Product Name',
        align: 'center',
        sortable: true,
        value: 'title'
      },
      {
        text: 'Purchase Type',
        align: 'center',
        sortable: false,
        value: 'purchaseType'
      },
      {
        text: 'Product Category',
        align: 'center',
        sortable: false,
        value: 'productGroup.name'
      },
      {
        text: 'Price',
        align: 'center',
        sortable: true,
        value: 'price'
      },
      {
        text: 'Created Date',
        align: 'center',
        sortable: true,
        value: 'createdAt'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: ''
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['createdAt'],
      sortDesc: [true],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  loading: {
    findAll: false
  }
});

export const state = () => initialState();

export type PartnerMerchantProductListState = ReturnType<typeof state>;

export const getters: GetterTree<PartnerMerchantProductListState, RootState> = {
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<PartnerMerchantProductListState> = {
  changeId: (state, id) => (state.id = id),
  changeData: (state, data) => (state.table.data = data),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableItems: (state, items) => {
    Object.assign(state.table, { items });
  },
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        title: filter.keyword,
        productGroupId: filter.productGroupId,
        purchaseType: filter.purchaseType
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setLoading: (state, loading: PartnerMerchantProductListState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<PartnerMerchantProductListState, RootState> = {
  /**
   * Fetch product.
   */
  async findAll({ commit, state, dispatch, rootGetters }) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      const variables = {
        ...state.query,
        where: {
          ...state.query.where,
          merchantId: rootGetters['partner/merchant/getId']
        }
      };
      const { data } = await api.query({
        query: gql`
          query ProductFindAll(
            $where: PurchaseProductQueryFilter
            $sort: PurchaseProductQuerySortBy
            $limit: Int
            $offset: Int
          ) {
            Purchase {
              id
              Product {
                findAll(
                  where: $where
                  sort: $sort
                  limit: $limit
                  offset: $offset
                ) {
                  count
                  results {
                    id
                    title
                    price
                    purchaseType
                    image
                    imageApp
                    imageWeb
                    types
                    productGroup {
                      id
                      name
                    }
                    createdAt
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const products = data?.Purchase?.Product?.findAll;
      commit('changeData', pick(products, ['count', 'results']));
    } catch (error) {
      if (!/signal is aborted without reason/i.test(error.toString()))
        emitter.emit('TOAST', error);
      commit('changeData', { count: 0, results: [] });
    } finally {
      commit('setLoading', { findAll: false });
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
